import React, { useState } from 'react';
import ImageViewer from './ImageViewer';
import { formatEventTime } from '../../utils/orderTimeline';
import { COLORS, FONTS } from '../../styles/tokens';

// Tidslinje nedover for et oppdrag: alle hendelser med klokkeslett, bilder, veieseddel og signatur.
// Samme komponent for bestiller og sjåfør. Eventene bygges av buildOrderTimeline.

const DOT_COLOR = {
  success: COLORS.success,
  warning: COLORS.warning,
  danger:  COLORS.danger,
};

export default function OrderTimeline({ events, role = 'orderer', loading = false, heading = 'Tidslinje', now }) {
  const [viewer, setViewer] = useState(null); // { url, label, signature }
  const reference = now ?? new Date();

  return (
    <section style={styles.section} aria-label={heading ? undefined : 'Tidslinje'} aria-labelledby={heading ? 'order-timeline-heading' : undefined} data-role={role}>
      {heading && <h2 id="order-timeline-heading" style={styles.heading}>{heading}</h2>}

      {loading ? (
        <div aria-busy="true" aria-label="Laster tidslinje" role="status">
          {[0, 1, 2].map(i => (
            <div key={i} style={styles.skelRow}>
              <span style={styles.skelDot} />
              <span style={{ flex: 1 }}>
                <span style={{ ...styles.skelBar, width: `${60 - i * 12}%` }} />
                <span style={{ ...styles.skelBar, width: '35%', height: 10, marginTop: 8 }} />
              </span>
            </div>
          ))}
        </div>
      ) : !events || events.length === 0 ? (
        <p style={styles.empty}>Ingen hendelser registrert</p>
      ) : (
        <ol style={styles.list}>
          {events.map((event, i) => {
            const last = i === events.length - 1;
            const time = formatEventTime(event.at, reference);
            return (
              <li key={event.key} style={styles.item}>
                <div style={styles.rail} aria-hidden="true">
                  <span style={{ ...styles.dot, background: DOT_COLOR[event.tone] ?? COLORS.primary }} />
                  {!last && <span style={styles.line} />}
                </div>
                <div style={{ ...styles.content, paddingBottom: last ? 0 : 18 }}>
                  <div style={styles.head}>
                    <b style={{ ...styles.title, color: event.tone === 'danger' ? COLORS.dangerText : COLORS.textDark }}>{event.title}</b>
                    {time && <time style={styles.time} dateTime={event.at.toISOString()}>{time}</time>}
                  </div>
                  {event.detail && <p style={styles.detail}>{event.detail}</p>}
                  <Media media={event.media} onOpen={setViewer} />
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {viewer && <ImageViewer {...viewer} onClose={() => setViewer(null)} />}
    </section>
  );
}

function Media({ media, onOpen }) {
  if (!media?.length) return null;
  const photos = media.filter(m => m.type === 'photo');
  const docs = media.filter(m => m.type === 'document');
  const signatures = media.filter(m => m.type === 'signature');
  return (
    <div style={styles.media}>
      {photos.length > 0 && (
        <div style={styles.row}>
          {photos.map(m => (
            <button key={m.url} type="button" style={styles.thumbBtn} onClick={() => onOpen({ url: m.url, label: m.label })} aria-label={`Åpne ${m.label.toLowerCase()}`}>
              <img src={m.url} alt={m.label} style={styles.thumb} loading="lazy" />
            </button>
          ))}
        </div>
      )}
      {docs.length > 0 && (
        <div style={styles.row}>
          {docs.map(m => (
            <a key={m.url} href={m.url} target="_blank" rel="noopener noreferrer" style={styles.chip}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" /><path d="M14 3v5h5" />
              </svg>
              {m.label}
              <span style={styles.srOnly}> (åpnes i ny fane)</span>
            </a>
          ))}
        </div>
      )}
      {signatures.map(m => (
        <button key={m.url} type="button" style={styles.signatureBtn} onClick={() => onOpen({ url: m.url, label: m.label, signature: true })} aria-label={`Åpne ${m.label.toLowerCase()}`}>
          <img src={m.url} alt={m.label} style={styles.signatureImg} loading="lazy" />
        </button>
      ))}
    </div>
  );
}

const styles = {
  section:      { margin: '18px 0 16px' },
  heading:      { fontFamily: FONTS.display, fontSize: 24, fontWeight: 700, color: COLORS.textDark, margin: '0 0 14px' },
  list:         { listStyle: 'none', margin: 0, padding: 0 },
  item:         { display: 'flex', gap: 12 },
  rail:         { display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 12 },
  dot:          { width: 12, height: 12, borderRadius: 9, marginTop: 4, flexShrink: 0 },
  line:         { flex: 1, width: 2, background: COLORS.border, marginTop: 2 },
  content:      { flex: 1, minWidth: 0 },
  head:         { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
  title:        { fontSize: 14, fontWeight: 700 },
  time:         { fontFamily: 'var(--font-mono)', fontSize: 10.5, color: COLORS.textLight, whiteSpace: 'nowrap' },
  detail:       { fontSize: 12.5, color: COLORS.textMid, margin: '3px 0 0', lineHeight: 1.45, overflowWrap: 'anywhere' },
  media:        { marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' },
  row:          { display: 'flex', flexWrap: 'wrap', gap: 8 },
  thumbBtn:     { padding: 0, border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: 'hidden', background: COLORS.surfaceHover, cursor: 'zoom-in', width: 64, height: 52 },
  thumb:        { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  chip:         { display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 32, padding: '0 12px', borderRadius: 16, border: `1px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, fontSize: 12.5, fontWeight: 600, textDecoration: 'none' },
  signatureBtn: { padding: 4, border: `1px solid ${COLORS.border}`, borderRadius: 10, background: '#ffffff', cursor: 'zoom-in', display: 'block', width: 120, maxWidth: '100%' },
  signatureImg: { width: '100%', height: 'auto', maxHeight: 56, objectFit: 'contain', display: 'block' },
  srOnly:       { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  empty:        { fontSize: 14, color: COLORS.textMid, margin: 0, padding: '8px 0' },
  skelRow:      { display: 'flex', gap: 12, padding: '4px 0 18px' },
  skelDot:      { width: 12, height: 12, borderRadius: 9, background: COLORS.skeleton, marginTop: 4, flexShrink: 0 },
  skelBar:      { display: 'block', height: 14, borderRadius: 6, background: COLORS.skeleton },
};
